import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Ticket, Gift, Wallet, ChevronRight, ShoppingBag } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const eur = (n) => `€${Number(n || 0).toFixed(2)}`;
const KIND = {
  walkin: { icon: Ticket, label: "Walk-in", cls: "bg-[#1e3a5f]/10 text-[#1e3a5f]" },
  coupon: { icon: Gift, label: "Coupon", cls: "bg-amber-100 text-amber-700" },
  topup: { icon: Wallet, label: "Top-up", cls: "bg-green-100 text-green-700" },
  desk: { icon: ShoppingBag, label: "Desk sale", cls: "bg-purple-100 text-purple-700" },
};
const FILTERS = [["all", "All"], ["walkin", "Walk-ins"], ["coupon", "Coupons"], ["topup", "Top-ups"], ["desk", "Desk sales"]];

function Row({ it, onOpen }) {
  const k = KIND[it.kind];
  const Icon = k.icon;
  const openable = !(it.kind === "desk" && !it.who);
  return (
    <button onClick={() => openable && onOpen(it)} disabled={!openable} className="w-full flex items-center gap-3 p-3 rounded-xl bg-white border hover:border-[#1e3a5f] text-left transition-colors" data-testid={`activity-row-${it.kind}-${it.ref}`}>
      <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${k.cls}`}><Icon className="w-4 h-4" /></span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#1e3a5f] truncate">{it.kind === "walkin" ? `${it.qty}× ${it.label}` : it.label}</p>
        <p className="text-xs text-slate-500 truncate">{it.who || "—"} · {new Date(it.at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}{it.by ? ` · ${it.by}` : ""}</p>
      </div>
      <div className="text-right shrink-0">
        <p className={`text-sm font-bold ${it.status === "voided" ? "line-through text-slate-400" : ""}`}>{eur(it.amount)}</p>
        <p className="text-[10px] text-slate-500 capitalize">{it.method}{it.fee > 0 ? ` +${eur(it.fee)}` : ""}{it.status === "voided" ? " · voided" : ""}</p>
      </div>
      {openable ? <ChevronRight className="w-4 h-4 text-slate-300" /> : <span className="w-4" />}
    </button>
  );
}

export default function RecentActivity({ eventId, onBack, onOpen }) {
  const { authAxios } = useAuth();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    authAxios.get(`/accountant/events/${eventId}/activity`).then(r => setRows(r.data))
      .catch(e => toast.error(e.response?.data?.detail || "Could not load activity"));
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = (rows || []).filter(r => filter === "all" || r.kind === filter);
  return (
    <div data-testid="recent-activity">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-600 mb-4"><ArrowLeft className="w-5 h-5" /> Back to Event</button>
      <h2 className="font-bold text-lg text-[#1e3a5f] mb-3">Recent sales</h2>
      <div className="flex gap-1.5 mb-3 overflow-x-auto">
        {FILTERS.map(([id, label]) => (
          <button key={id} onClick={() => setFilter(id)} data-testid={`activity-filter-${id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${filter === id ? "bg-[#1e3a5f] text-white" : "bg-white border text-slate-600"}`}>{label}</button>
        ))}
      </div>
      {rows === null ? <p className="text-sm text-slate-400 text-center py-6">Loading…</p>
        : shown.length === 0 ? <p className="text-sm text-slate-400 text-center py-6" data-testid="activity-empty">No sales yet.</p>
        : <div className="space-y-2" data-testid="activity-list">{shown.map(it => <Row key={`${it.kind}-${it.ref}-${it.at}`} it={it} onOpen={onOpen} />)}</div>}
    </div>
  );
}
